import React from "react";
import { pageTitle } from "../../helpers/PageTitle";
import Section from "../Section";
import Spacing from "../Spacing";
import {
  BsArrowRight, BsShieldCheck, BsPhone, BsHeartPulse,
  BsPeople, BsDroplet, BsCalendarCheck, BsAward,
  BsCapsule, BsJournalMedical, BsCheckCircleFill
} from "react-icons/bs";

const ecosystemData = [
  { title: "Preventive Healthcare", icon: <BsHeartPulse />, desc: "Proactive screening for long-term wellness." },
  { title: "Digital Health Access", icon: <BsPhone />, desc: "Telemedicine and smart scheduling." },
  { title: "Community Support", icon: <BsPeople />, desc: "Blood donation and local health drives." },
  { title: "Patient Rewards", icon: <BsAward />, desc: "GKCM tokens for healthy activities." },
];

const coreServices = [
  { title: "Health Packages", icon: <BsCapsule />, desc: "Curated preventive healthcare packages designed for all stages of life." },
  { title: "Blood Support", icon: <BsDroplet />, desc: "A community-based donation initiative recorded through our app." },
  { title: "Telemedicine", icon: <BsPhone />, desc: "Consult experienced doctors anywhere through the GKC Klinica app." },
  { title: "Smart Booking", icon: <BsCalendarCheck />, desc: "Easily schedule appointments with preferred doctors instantly." },
  { title: "GKCM Rewards", icon: <BsAward />, desc: "Earn tokens for health activities and redeem ecosystem benefits." },
  { title: "Insurance Help", icon: <BsShieldCheck />, desc: "Guidance for insurance documentation and claim procedures." },
  { title: "Digital Records", icon: <BsJournalMedical />, desc: "Securely store prescriptions and medical history for easy access." },
  { title: "Yoga & Wellness", icon: <BsHeartPulse />, desc: "Lifestyle programs including yoga and stress management." },
  { title: "Community Health", icon: <BsPeople />, desc: "Regular donation drives and public health awareness camps." },
];

export default function Services() {
  pageTitle("Our Services");

  return (
    <div className="cs_services_page">

      {/* 1. HERO SECTION */}
      <Section className="cs_hero_section"
        topMd={150}
        topLg={70}
        topXl={105}
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 text-center text-lg-start">
              <h1 className="fw-bold">A Complete Healthcare Ecosystem</h1>
              <p className="lead text-muted mt-4 pe-lg-5">
                At GKC Klinica, we integrate prevention, digital access, and
                community support into one seamless experience designed for your well-being.
              </p>
              <div className="d-flex flex-wrap justify-content-center justify-content-lg-start gap-3 mt-5">

                <button className="gk_btn gk_btn_primary" onClick={() => (window.location.href = "/")}>
                  Explore Services
                </button>

                <button className="gk_btn gk_btn_outline" onClick={() =>
                  window.open(
                    "https://play.google.com/store/apps/details?id=com.gkcklinica.GkcKlinica&hl=en_IN",
                    "_blank"
                  )
                }>
                  Download App <BsArrowRight />
                </button>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="cs_hero_img_box mt-5 mt-lg-0">
                <img src="/images/service/service-community.png" alt="Healthcare" className="img-fluid w-100" />
                <div className="cs_floating_label cs_label_1">Verified Doctors</div>
                <div className="cs_floating_label cs_label_2">Secure Data</div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 2. ECOSYSTEM JOURNEY */}
      <Section className="cs_section_padding cs_gray_bg">
        <div className="container text-center">
          <h2 className="display-6 fw-bold mb-3">Healthcare That Works Together</h2>
          <p className="text-muted mx-auto col-lg-7 mb-5">
            Our structured ecosystem connects medical care and digital tools into one organized system.
          </p>
          <div className="row g-4">
            {ecosystemData.map((item, i) => (
              <div className="col-xl-3 col-md-6" key={i}>
                <div className="cs_journey_card">
                  <div className="cs_journey_icon">{item.icon}</div>
                  <h4 className="h5 fw-bold mb-3" style={{ color: "#000" }}>{item.title}</h4>
                  <p className="text-muted small mb-0" style={{ color: "#000000e0" }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 3. CORE SERVICES GRID */}
      <Section className="cs_section_padding">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold">Our Core Services</h2>
          </div>
          <div className="row g-4">
            {coreServices.map((service, i) => (
              <div className="col-lg-4 col-md-6" key={i}>
                <div className="cs_service_card_v2 h-100">
                  <div className="cs_card_icon">{service.icon}</div>
                  <h3 className="h5 fw-bold mb-3">{service.title}</h3>
                  <p className="text-muted small mb-0">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 6. COMMUNITY IMPACT (ENHANCED DARK SECTION) */}
      <Section className="cs_section_padding cs_community_section text-white text-center">
        <div className="container">
          <div className="cs_community_impact_card p-4 p-md-5 rounded-5">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <span className="badge bg-white text-primary px-3 py-2 rounded-pill mb-3 fw-bold shadow-sm">Community Impact</span>
                <h2 className="display-5 fw-bold mb-3 text-white opacity-75">Healthcare Beyond the Clinic</h2>
                <p className="opacity-75 mb-5 lead">
                  Providing awareness, early detection, and life-saving initiatives
                  where it matters most — in your local community.
                </p>
              </div>
            </div>
            <div className="row g-3 g-md-4 justify-content-center">
              {[
                { label: 'Blood Donation', icon: <BsDroplet /> },
                { label: 'Health Camps', icon: <BsCalendarCheck /> },
                { label: 'Public Awareness', icon: <BsPeople /> },
                { label: 'Wellness Initiatives', icon: <BsHeartPulse /> }
              ].map((item, i) => (
                <div className="col-6 col-lg-3" key={i}>
                  <div className="cs_impact_item p-4 h-100 rounded-4">
                    <div className="cs_impact_icon mb-3">{item.icon}</div>
                    <h6 className="mb-0 fw-bold text-white opacity-75">{item.label}</h6>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5">
              <button className="btn btn-outline-light rounded-pill px-5 py-3 fw-bold" onClick={() => (window.location.href = "/")}>
                Learn More About Our Programs <BsArrowRight className="ms-2" />
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* 4. APP FEATURE HIGHLIGHT */}

      <Section
        className="cs_section_padding"
        style={{
          background: "linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)"
        }}
      >
        <div className="container">
          <div className="row align-items-center g-5">

            {/* Image Section */}
            <div className="col-lg-5 order-1 order-lg-0 text-center">
              <div className="app-image-wrapper">
                <img
                  src="/images/service/service page-banner.png"
                  alt="App"
                  className="img-fluid"
                />
              </div>
            </div>

            {/* Content Section */}
            <div className="col-lg-7 text-center text-lg-start">

              <h2 className="app-heading">
                One App. Complete Healthcare Access.
              </h2>

              <p className="app-subtext">
                Manage your healthcare journey seamlessly with our all-in-one platform designed for convenience, security, and efficiency.
              </p>

              <ul className="app-feature-list">
                {[
                  'Online doctor consultations',
                  'Instant appointment booking',
                  'Secure digital medical records',
                  'Blood donation support system',
                  'Health package management',
                  'GKCM reward tokens wallet'
                ].map((text, i) => (
                  <li key={i}>
                    <BsCheckCircleFill className="icon" />
                    {text}
                  </li>
                ))}
              </ul>

              <button className="app-btn" onClick={() =>
                window.open(
                  "https://play.google.com/store/apps/details?id=com.gkcklinica.GkcKlinica&hl=en_IN",
                  "_blank"
                )
              }>
                Download Now
              </button>

            </div>
          </div>
        </div>
      </Section>
      {/* 7. ECOSYSTEM SUMMARY PILLS */}
      <Section className="cs_section_padding">
        <div className="container text-center">
          <div className="cs_summary_box p-4 p-md-5 rounded-4 bg-light border">
            <h2 className="fw-bold h3 mb-5">One System. One App. Complete Care.</h2>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              {['Consultations', 'Preventive Packages', 'Digital Records', 'Community Initiatives', 'GKCM Rewards'].map((text, i) => (
                <div key={i} className="cs_summary_badge">
                  <BsCheckCircleFill className="text-primary" /> {text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* 8. FINAL CTA */}
      <Section className="pb-5 text-center">
        <div className="container">
          <div className="cs_cta_box rounded-5">
            <h2 className="text-white display-6 fw-bold">Start Your Healthcare Journey</h2>
            <p className="text-white-50 mt-3 mb-5 lead">Experience the future of connected healthcare with GKC Klinica today.</p>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              <button className="btn btn-light btn-lg px-5 py-3 fw-bold shadow" onClick={() => window.location.href = "https://app.gkcklinica.com/public-profiles"}>Book Consultation</button>
              <button className="btn btn-outline-light btn-lg px-5 py-3 fw-bold" onClick={() => window.location.href = "https://play.google.com/store/apps/details?id=com.gkcklinica.GkcKlinica&hl=en_IN"}>Get the App</button>
            </div>
          </div>
        </div>
      </Section>

    </div>
  );
}